import { createAction, handleActions } from 'redux-actions';
import { setDialogVisibility } from './dialogs';
import {
  getItemIndex,
  orderByPage,
  removeObjectFromArray,
  replaceObjectInList,
} from '../../utility/helpers';
import http from '../../utility/http';
import { isSubscriptionExpiredCheck } from '../../utility/subscriptionHelper';
import {
  API_ORGANIZATION_SETTINGS,
  API_PRESETS,
  api_preset,
  api_page_tag_categories,
} from '../../constants/apiRoutes';
import { PLACEMENTS } from '../../constants/tags';

// ------------------------------------
// Constants
// ------------------------------------

const SET_ORGANIZATION_SETTINGS = 'SET_ORGANIZATION_SETTINGS';
const SET_ORGANIZATION_MENU_SETUP = 'SET_ORGANIZATION_MENU_SETUP';
const EDIT_ORGANIZATION_MENU_SETUP = 'EDIT_ORGANIZATION_MENU_SETUP';
const DELETE_ORGANIZATION_MENU_SETUP = 'DELETE_ORGANIZATION_MENU_SETUP';
const SET_PRESETS = 'SET_PRESETS';
const ADD_PRESET = 'ADD_PRESET';
const EDIT_PRESET = 'EDIT_PRESET';
const DELETE_PRESET = 'DELETE_PRESET';
const SET_ACTIVE_PRESET = 'SET_ACTIVE_PRESET';

const initialState = {
  organizationSettings: {},
  organizationMenuSetup: [],
  presets: {},
};

// ------------------------------------
// Actions
// ------------------------------------

export const setOrganizationSettings = createAction(SET_ORGANIZATION_SETTINGS);
const setOrganizationMenuSetup = createAction(SET_ORGANIZATION_MENU_SETUP);
const setPresets = createAction(SET_PRESETS);
const addPreset = createAction(ADD_PRESET);
const editPreset = createAction(EDIT_PRESET);
const deletePreset = createAction(DELETE_PRESET);
const setActivePreset = createAction(SET_ACTIVE_PRESET);
export const editOrganizationMenuSetup = createAction(
  EDIT_ORGANIZATION_MENU_SETUP
);
export const deleteOrganizationMenuSetup = createAction(
  DELETE_ORGANIZATION_MENU_SETUP
);

// ------------------------------------
// Reducers
// ------------------------------------

const reducers = {
  [SET_ORGANIZATION_SETTINGS]: (state, { payload }) => {
    return {
      ...state,
      organizationSettings: { ...state.organizationSettings, ...payload },
    };
  },
  [SET_ORGANIZATION_MENU_SETUP]: (state, { payload }) => {
    return {
      ...state,
      organizationMenuSetup: payload,
    };
  },
  [SET_PRESETS]: (state, { payload }) => {
    return {
      ...state,
      presets: orderByPage(payload),
    };
  },
  [ADD_PRESET]: (state, { payload }) => {
    const { presets } = state;
    const { location } = payload;

    return {
      ...state,
      presets: {
        ...presets,
        [location]: presets[location]
          ? [payload].concat(presets[location])
          : [payload],
        active: { ...presets.active, [location]: payload.id },
      },
    };
  },
  [EDIT_PRESET]: (state, { payload }) => {
    const { presets } = state;
    const { location } = payload;

    const presetIndex = getItemIndex(presets[location], payload.id);

    return {
      ...state,
      presets: {
        ...presets,
        [location]: replaceObjectInList(
          presets[location],
          presetIndex,
          payload
        ),
      },
    };
  },
  [DELETE_PRESET]: (state, { payload }) => {
    const { presets } = state;
    const { presetLocation } = payload;
    const filteredPresets = {
      ...presets,
      [presetLocation]: removeObjectFromArray(presets[presetLocation], payload),
    };

    return { ...state, presets: filteredPresets };
  },
  [SET_ACTIVE_PRESET]: (state, { payload }) => {
    const { presets } = state;
    const { id, locationId, isActive } = payload;
    const activePreset = isActive ? id : -1;

    return {
      ...state,
      presets: {
        ...presets,
        active: { ...presets.active, [locationId]: activePreset },
      },
    };
  },
  [EDIT_ORGANIZATION_MENU_SETUP]: (state, { payload }) => {
    const { organizationMenuSetup } = state;
    const index = organizationMenuSetup.findIndex(
      category => category.id === payload.id
    );
    const organizationMenuCategories =
      index === -1
        ? organizationMenuSetup.concat(payload)
        : replaceObjectInList(organizationMenuSetup, index, payload);

    return { ...state, organizationMenuSetup: organizationMenuCategories };
  },
  [DELETE_ORGANIZATION_MENU_SETUP]: (state, { payload }) => {
    const { organizationMenuSetup } = state;
    const organizationMenuCategories = organizationMenuSetup.filter(
      category => category.id !== payload.id
    );

    return { ...state, organizationMenuSetup: organizationMenuCategories };
  },
};

// ------------------------------------
// API calls
// ------------------------------------

export const getAllPresets = dispatch => {
  return http.get(API_PRESETS).then(({ data }) => {
    dispatch(setPresets(data));
  });
};

export const getOrganizationSettings = async dispatch => {
  const { data } = await http.get(API_ORGANIZATION_SETTINGS);
  const isSubscriptionExpired = isSubscriptionExpiredCheck(data);

  if (isSubscriptionExpired) {
    dispatch(
      setDialogVisibility({
        dialogName: 'upgradeRequiredDialog',
        opened: true,
      })
    );
  }

  dispatch(setOrganizationSettings(data));

  return Promise.resolve(isSubscriptionExpired);
};

export const getOrganizationMenuSetup = dispatch => {
  return http
    .get(api_page_tag_categories(PLACEMENTS.ORGANIZATION_MENU.id))
    .then(({ data }) => {
      dispatch(setOrganizationMenuSetup(data));
    });
};

export const addNewPreset = (dispatch, params) => {
  return http.post(API_PRESETS, params).then(({ data }) => {
    dispatch(addPreset(data));

    return data;
  });
};

export const onEditPreset = (dispatch, preset) => {
  const { id, ...rest } = preset;

  return http.patch(api_preset(id), { ...rest }).then(({ data }) => {
    return dispatch(editPreset(data));
  });
};

export const deleteSelectedPreset = (dispatch, presetId, locationId) => {
  return http.delete(`${API_PRESETS}${presetId}/`).then(() => {
    dispatch(deletePreset({ id: presetId, presetLocation: locationId }));
  });
};

export const editPresetActivity = (
  dispatch,
  presetId,
  locationId,
  isActive
) => {
  dispatch(setActivePreset({ id: presetId, locationId, isActive }));
};

export const updateOrganizationSettings = (dispatch, params) => {
  return http.post(API_ORGANIZATION_SETTINGS, params).then(({ data }) => {
    dispatch(setOrganizationSettings(data));
  });
};

// ------------------------------------
// "plain" action functions
// ------------------------------------

export const clearAllPresets = () => setPresets([]);

export default handleActions(reducers, initialState);
